import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit, 
  startAfter,
  getDoc, 
  doc 
} from 'firebase/firestore';
import { ref } from 'firebase/storage';
import { firestore, storage } from '../../firebase/config';
import JobCard from '../cards/JobCard';

const JobListPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse query parameters from the URL
  const searchParams = new URLSearchParams(location.search);
  const initialSearchQuery = searchParams.get('query') || '';
  const initialLocationQuery = searchParams.get('location') || '';
  
  // State variables
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [locationQuery, setLocationQuery] = useState(initialLocationQuery);
  const [showFilters, setShowFilters] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    jobType: '',
    experienceLevel: '',
    salaryRange: ''
  });
  
  // Fetch jobs on component mount and when search parameters change
  useEffect(() => {
    fetchJobs();
  }, [initialSearchQuery, initialLocationQuery]);
  
  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    
    // Update URL with search parameters
    const params = new URLSearchParams();
    if (searchQuery) params.set('query', searchQuery);
    if (locationQuery) params.set('location', locationQuery);
    
    navigate({
      pathname: '/dashboard/jobs',
      search: params.toString()
    });
    
    // Reset pagination and fetch new results
    setLastDoc(null);
    setHasMore(true);
    fetchJobs(true);
  };
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    setFilterOptions({
      ...filterOptions,
      [e.target.name]: e.target.value
    });
  };
  
  // Reset all filters
  const resetFilters = () => {
    setFilterOptions({
      jobType: '',
      experienceLevel: '',
      salaryRange: ''
    });
    setSearchQuery('');
    setLocationQuery('');
    
    // Update URL to remove search parameters
    navigate('/dashboard/jobs');
    
    // Fetch fresh data with no filters
    setLastDoc(null);
    setHasMore(true);
    fetchJobs(true);
  };
  
  // Apply client-side filters based on filterOptions
  const applyClientFilters = (jobsData) => {
    let filtered = [...jobsData];
    
    // First apply search query filters
    if (searchQuery) {
      console.log(`Applying search query filter at render time: "${searchQuery}"`);
      const searchTerms = searchQuery.toLowerCase().split(' ').filter(term => term.length > 0);
      
      filtered = filtered.filter(job => {
        // Check multiple fields for matches
        const titleMatch = job.title && searchTerms.some(term => 
          job.title.toLowerCase().includes(term));
        
        const descriptionMatch = job.description && searchTerms.some(term => 
          job.description.toLowerCase().includes(term));
          
        const businessNameMatch = job.businessName && searchTerms.some(term => 
          job.businessName.toLowerCase().includes(term));
          
        const jobTypeMatch = job.jobType && searchTerms.some(term => 
          job.jobType.toLowerCase().includes(term));
        
        return titleMatch || descriptionMatch || businessNameMatch || jobTypeMatch;
      });
      
      console.log(`After search query filter: ${filtered.length} jobs`);
    }
    
    // Apply location filter
    if (locationQuery) {
      console.log(`Applying location filter at render time: "${locationQuery}"`);
      const locationTerms = locationQuery.toLowerCase().split(' ').filter(term => term.length > 0);
      
      filtered = filtered.filter(job => {
        // Check location fields for matches
        const locationMatch = job.location && locationTerms.some(term => 
          job.location.toLowerCase().includes(term));
          
        const addressMatch = job.address && locationTerms.some(term => 
          job.address.toLowerCase().includes(term));
        
        return locationMatch || addressMatch;
      });
      
      console.log(`After location filter: ${filtered.length} jobs`);
    }
    
    // Filter by job type
    if (filterOptions.jobType) {
      filtered = filtered.filter(job => 
        job.jobType && job.jobType.toLowerCase().includes(filterOptions.jobType.toLowerCase())
      );
      console.log(`After job type filter (${filterOptions.jobType}): ${filtered.length} jobs`);
    }
    
    // Filter by experience level
    if (filterOptions.experienceLevel) {
      filtered = filtered.filter(job => 
        job.experienceLevel && job.experienceLevel.toLowerCase().includes(filterOptions.experienceLevel.toLowerCase())
      );
      console.log(`After experience level filter (${filterOptions.experienceLevel}): ${filtered.length} jobs`);
    }
    
    // Filter by salary range
    if (filterOptions.salaryRange) {
      const [min, max] = filterOptions.salaryRange.split('-').map(Number);
      filtered = filtered.filter(job => {
        // Extract numeric value from salary
        const salaryNum = parseFloat(job.salary?.replace(/[^0-9.]/g, ''));
        if (isNaN(salaryNum)) return false;
        
        if (max) {
          return salaryNum >= min && salaryNum <= max;
        } else {
          // For "75+" option
          return salaryNum >= min;
        }
      });
      console.log(`After salary range filter (${filterOptions.salaryRange}): ${filtered.length} jobs`);
    }
    
    return filtered;
  };
  
  // Fetch jobs from Firestore
  const fetchJobs = async (isNewSearch = false) => {
    try {
      setLoading(isNewSearch || jobs.length === 0);
      console.log('Fetching jobs...');
      
      // Collection reference
      const jobsRef = collection(firestore, 'jobs');
      
      // Start with a basic query to get all jobs first, then filter client-side
      // This is more reliable when we're not sure about the data structure
      let jobsQuery = query(
        jobsRef,
        orderBy('createdAt', 'desc'),
        limit(30) // Increase limit to get more results
      );
      
      console.log('Executing basic job query to fetch initial data');
      const jobsSnapshot = await getDocs(jobsQuery);
      
      console.log(`Retrieved ${jobsSnapshot.size} total jobs from database`);
      
      // Log structure of the first document to debug
      if (jobsSnapshot.size > 0) {
        const sampleData = jobsSnapshot.docs[0].data();
        console.log('Sample job document structure:', sampleData);
        console.log('Sample job fields:', Object.keys(sampleData));
        
        // Save last document for pagination
        setLastDoc(jobsSnapshot.docs[jobsSnapshot.docs.length - 1]);
      }
      
      if (jobsSnapshot.empty) {
        console.log('No jobs found in the database at all - database may be empty');
        setHasMore(false);
        setLoading(false);
        setLoadingMore(false);
        setJobs([]);
        return;
      }
      
      // Build a set of unique business IDs to query
      const businessIds = new Set();
      for (const jobDoc of jobsSnapshot.docs) {
        const jobData = jobDoc.data();
        if (jobData.businessId) {
          businessIds.add(jobData.businessId);
        }
      }
      console.log(`Found ${businessIds.size} unique businesses for jobs`);
      
      // Fetch all businesses in parallel
      const businessDataMap = {};
      await Promise.all(
        Array.from(businessIds).map(async (businessId) => {
          try {
            const businessDoc = await getDoc(doc(firestore, 'businesses', businessId));
            if (businessDoc.exists()) {
              businessDataMap[businessId] = businessDoc.data();
            }
          } catch (err) {
            console.error(`Error fetching business data for ID ${businessId}:`, err);
          }
        })
      );
      
      // Process all jobs first
      const allJobs = jobsSnapshot.docs.map(doc => processJobData(doc, businessDataMap));
      console.log(`Processed ${allJobs.length} jobs`);
      
      // Now filter the jobs based on search criteria on the client side
      let filteredJobs = allJobs;
      
      if (searchQuery) {
        console.log(`Filtering jobs by search query: "${searchQuery}"`);
        const searchTerms = searchQuery.toLowerCase().split(' ').filter(term => term.length > 0);
        
        filteredJobs = allJobs.filter(job => {
          // Check multiple fields for matches
          const titleMatch = job.title && searchTerms.some(term => 
            job.title.toLowerCase().includes(term));
          
          const descriptionMatch = job.description && searchTerms.some(term => 
            job.description.toLowerCase().includes(term));
            
          const businessNameMatch = job.businessName && searchTerms.some(term => 
            job.businessName.toLowerCase().includes(term));
            
          const jobTypeMatch = job.jobType && searchTerms.some(term => 
            job.jobType.toLowerCase().includes(term));
          
          return titleMatch || descriptionMatch || businessNameMatch || jobTypeMatch;
        });
        
        console.log(`Found ${filteredJobs.length} jobs matching search query "${searchQuery}"`);
      }
      
      if (locationQuery) {
        console.log(`Filtering jobs by location: "${locationQuery}"`);
        const locationTerms = locationQuery.toLowerCase().split(' ').filter(term => term.length > 0);
        
        filteredJobs = filteredJobs.filter(job => {
          // Check location fields for matches
          const locationMatch = job.location && locationTerms.some(term => 
            job.location.toLowerCase().includes(term));
            
          const addressMatch = job.address && locationTerms.some(term => 
            job.address.toLowerCase().includes(term));
          
          return locationMatch || addressMatch;
        });
        
        console.log(`Found ${filteredJobs.length} jobs matching location "${locationQuery}"`);
      }
      
      // Apply other filters after the basic searches
      filteredJobs = applyClientFilters(filteredJobs);
      
      if (filteredJobs.length === 0) {
        console.log('No jobs found matching all criteria after filtering');
      } else {
        console.log(`Final filtered results: ${filteredJobs.length} jobs`);
      }
      
      // Update jobs state
      if (isNewSearch) {
        setJobs(filteredJobs);
      } else {
        // Create a Set of existing job IDs for better performance on lookups
        const existingIds = new Set(jobs.map(job => job.id));
        // Only add jobs that don't already exist in our list
        const newUniqueJobs = filteredJobs.filter(job => !existingIds.has(job.id));
        console.log(`Adding ${newUniqueJobs.length} unique new jobs`);
        
        if (newUniqueJobs.length > 0) {
          setJobs(prevJobs => [...prevJobs, ...newUniqueJobs]);
        }
      }
      
      // If we got fewer than the requested limit, there's no more data
      setHasMore(jobsSnapshot.size >= 12); 
      setLoading(false);
      setLoadingMore(false);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  // Helper function to process job data consistently
  const processJobData = (doc, businessDataMap) => {
    const jobData = doc.data();
    const jobId = doc.id;
    
    // Get business data from our map
    const businessData = businessDataMap[jobData.businessId] || {};
    
    // Calculate posted days
    let postedDays = 0;
    try {
      const postedDate = jobData.createdAt?.__time__ 
        ? new Date(jobData.createdAt.__time__) 
        : jobData.createdAt?.toDate?.() || new Date();
        
      const currentDate = new Date();
      const diffTime = currentDate - postedDate;
      postedDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (err) {
      console.warn('Error calculating posted days', err);
    }
    
    // Get job location from the address object
    const jobLocation = 
      (jobData.address && jobData.address.city) ? jobData.address.city : 
      (businessData.address && businessData.address.city) ? businessData.address.city : 
      (businessData.location && businessData.location.city) ? businessData.location.city : 
      'Unknown Location';
    
    // Format salary
    let formattedSalary = 'Salary not specified';
    if (jobData.hourlyRate) {
      const hourlyRate = typeof jobData.hourlyRate === 'number' 
          ? jobData.hourlyRate 
          : parseFloat(jobData.hourlyRate);
      
      if (!isNaN(hourlyRate)) {
        if (hourlyRate > 1000) {
          formattedSalary = `$${(hourlyRate/1000).toFixed(0)}k/year`;
        } else {
          formattedSalary = `$${hourlyRate.toFixed(2)}/hr`;
        }
      }
    } else if (jobData.salary) {
      formattedSalary = jobData.salary;
    }
    
    // Get business image path
    let businessImagePath = null;
    if (businessData.businessImage && businessData.businessImage.path) {
      businessImagePath = businessData.businessImage.path;
    }
    
    // Build complete job object with all needed fields
    return {
      id: jobId,
      title: jobData.title || 'Untitled Position',
      businessName: businessData.name || 'Unknown Business',
      location: jobLocation,
      salary: formattedSalary,
      postedDays: postedDays,
      description: jobData.description || 'No description available',
      address: jobData.address?.street 
        ? `${jobData.address.city || ''}, ${jobData.address.state || ''} ${jobData.address.zipcode || ''}`
        : (businessData.address?.street || 'Address not specified'),
      pay: formattedSalary,
      jobType: jobData.jobType || 'Not specified',
      experienceLevel: jobData.experienceLevel || 'Not specified',
      businessId: jobData.businessId,
      businessLogo: businessImagePath || businessData.logoUrl || null,
      businessImagePath: businessImagePath,
      immediateStart: jobData.isImmediateStart,
      applicationUrl: jobData.applicationUrl || null,
      // More comprehensive search terms
      searchTerms: `${jobData.title || ''} ${businessData.name || ''} ${jobData.description || ''} ${jobData.jobType || ''} ${jobLocation || ''}`.toLowerCase()
    };
  };
  
  // Load more jobs
  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    
    if (!lastDoc) {
      console.error("Cannot load more: No reference document available");
      setHasMore(false);
      return;
    }
    
    setLoadingMore(true);
    fetchMoreJobs();
  };
  
  // Separate function to fetch more jobs for pagination
  const fetchMoreJobs = async () => {
    try {
      // Validate we have a last document reference
      if (!lastDoc) {
        console.log('No last document reference for pagination');
        setLoadingMore(false);
        setHasMore(false);
        return;
      }
      
      const jobsRef = collection(firestore, 'jobs');
      
      // Create a properly paginated query
      const paginatedQuery = query(
        jobsRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),  // Use the last document for proper pagination
        limit(15)  // Get next batch
      );
      
      console.log('Executing paginated query to fetch more jobs');
      const jobsSnapshot = await getDocs(paginatedQuery);
      
      if (jobsSnapshot.empty) {
        console.log('No more jobs to load');
        setHasMore(false);
        setLoadingMore(false);
        return;
      }
      
      // Save last document for next pagination
      setLastDoc(jobsSnapshot.docs[jobsSnapshot.docs.length - 1]);
      
      // Get business data for these jobs
      const businessIds = new Set();
      for (const jobDoc of jobsSnapshot.docs) {
        const jobData = jobDoc.data();
        if (jobData.businessId) {
          businessIds.add(jobData.businessId);
        }
      }
      
      const businessDataMap = {};
      await Promise.all(
        Array.from(businessIds).map(async (businessId) => {
          try {
            const businessDoc = await getDoc(doc(firestore, 'businesses', businessId));
            if (businessDoc.exists()) {
              businessDataMap[businessId] = businessDoc.data();
            }
          } catch (err) {
            console.error(`Error fetching business data for ID ${businessId}:`, err);
          }
        })
      );
      
      // Process the new jobs
      const newJobs = jobsSnapshot.docs.map(doc => processJobData(doc, businessDataMap));
      console.log(`Fetched ${newJobs.length} additional jobs`);
      
      // Filter the new jobs based on search criteria
      let filteredNewJobs = newJobs;
      
      if (searchQuery) {
        const searchTerms = searchQuery.toLowerCase().split(' ').filter(term => term.length > 0);
        
        filteredNewJobs = newJobs.filter(job => {
          const titleMatch = job.title && searchTerms.some(term => 
            job.title.toLowerCase().includes(term));
          
          const descriptionMatch = job.description && searchTerms.some(term => 
            job.description.toLowerCase().includes(term));
            
          const businessNameMatch = job.businessName && searchTerms.some(term => 
            job.businessName.toLowerCase().includes(term));
            
          const jobTypeMatch = job.jobType && searchTerms.some(term => 
            job.jobType.toLowerCase().includes(term));
          
          return titleMatch || descriptionMatch || businessNameMatch || jobTypeMatch;
        });
      }
      
      if (locationQuery) {
        const locationTerms = locationQuery.toLowerCase().split(' ').filter(term => term.length > 0);
        
        filteredNewJobs = filteredNewJobs.filter(job => {
          const locationMatch = job.location && locationTerms.some(term => 
            job.location.toLowerCase().includes(term));
            
          const addressMatch = job.address && locationTerms.some(term => 
            job.address.toLowerCase().includes(term));
          
          return locationMatch || addressMatch;
        });
      }
      
      // Apply filters to new jobs
      filteredNewJobs = applyClientFilters(filteredNewJobs);
      
      if (filteredNewJobs.length === 0) {
        console.log('No jobs found matching all criteria after filtering new batch');
        // Still try to load more if we have data that didn't match filters
        if (jobsSnapshot.size >= 15) {
          console.log('Will try to fetch more jobs since current batch had no matches');
          fetchMoreJobs();
          return;
        } else {
          setHasMore(false);
        }
      } else {
        console.log(`Final filtered results from new batch: ${filteredNewJobs.length} jobs`);
      }
      
      // Create a Set with ALL current job IDs for efficient lookup
      const existingIds = new Set(jobs.map(job => job.id));
      // Filter out any jobs that already exist in our state
      const uniqueNewJobs = filteredNewJobs.filter(job => !existingIds.has(job.id));
      
      console.log(`Adding ${uniqueNewJobs.length} unique new jobs after filtering`);
      
      // Update jobs state by appending unique new jobs
      if (uniqueNewJobs.length > 0) {
        setJobs(prevJobs => [...prevJobs, ...uniqueNewJobs]);
        setHasMore(true);
      } else {
        // If we got jobs from the database but none were unique, try loading more
        if (jobsSnapshot.size > 0 && jobsSnapshot.size >= 15) {
          console.log('No unique jobs found in this batch, trying to fetch more');
          fetchMoreJobs();
          return;
        } else {
          setHasMore(false);
        }
      }
      
      setLoadingMore(false);
    } catch (error) {
      console.error('Error fetching more jobs:', error);
      setLoadingMore(false);
      setHasMore(false);
    }
  };
  
  // Handle job application
  const handleApplyJob = async (jobId) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    try {
      console.log(`Applying for job ${jobId} using ApplicationService...`);
      
      // Find the job to get businessId
      const job = jobs.find(j => j.id === jobId);
      if (!job) {
        alert('Job not found. Please try again.');
        return;
      }
      
      console.log(`Job details:`, { jobId, businessId: job.businessId, title: job.title });
      
      // Validate we have both jobId and businessId
      if (!job.businessId) {
        console.error('❌ Job is missing businessId:', job);
        alert('Error: Job is missing business information. Please try again.');
        return;
      }
      
      // Handle external applications (open URL)
      if (job.applicationUrl) {
        console.log('External application:', job.applicationUrl);
        window.open(job.applicationUrl, '_blank');
        return;
      }
      
      // Use ApplicationService to handle internal application
      const ApplicationService = (await import('../../services/ApplicationService')).ApplicationService;
      const result = await ApplicationService.submitApplication({
        userId: currentUser.uid,
        jobId: jobId,
        businessId: job.businessId
      });
      
      if (result.success) {
        // alert(result.message || 'Application submitted successfully!'); // Disabled notification
      } else {
        alert(result.message || 'Failed to submit application');
      }
      
    } catch (error) {
      console.error('Error applying for job:', error);
      alert('Failed to submit application. Please try again.');
    }
  };
  
  // Apply client-side filters
  const filteredJobs = applyClientFilters(jobs);
  
  return (
    <div className="flex-1 bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Browse Jobs
            </h1>
            <p className="text-gray-600">Find the perfect job opportunity</p>
          </div>
          <button 
            onClick={() => navigate('/dashboard/businesses')}
            className="px-4 py-2 text-sm bg-white border border-[#FF8C00] text-[#FF8C00] rounded hover:bg-[#FF8C00]/10 transition flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            View Businesses
          </button>
        </div>
        
        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <form onSubmit={handleSearch}>
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Jobs, key words, or company"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent transition-all"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Locations"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent transition-all"
                  value={locationQuery}
                  onChange={(e) => setLocationQuery(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors flex-shrink-0 font-medium"
              >
                Search Jobs
              </button>
            </div>
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="text-sm text-[#FF8C00] hover:text-[#FF8C00]/80 flex items-center transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                </svg>
                {showFilters ? 'Hide filters' : 'Show filters'}
              </button>
              <button
                type="button"
                onClick={resetFilters}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Reset filters
              </button>
            </div>
            
            {/* Filters Section */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                  <select 
                    name="jobType"
                    value={filterOptions.jobType}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
                  >
                    <option value="">All Types</option>
                    <option value="fulltime">Full-time</option>
                    <option value="parttime">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="temporary">Temporary</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Experience Level</label>
                  <select 
                    name="experienceLevel"
                    value={filterOptions.experienceLevel}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
                  >
                    <option value="">Any Experience</option>
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salary Range</label>
                  <select 
                    name="salaryRange"
                    value={filterOptions.salaryRange}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
                  >
                    <option value="">Any Salary</option>
                    <option value="0-25">$0 - $25/hr</option>
                    <option value="25-50">$25 - $50/hr</option>
                    <option value="50-75">$50 - $75/hr</option>
                    <option value="75">$75+/hr</option>
                  </select>
                </div>
              </div>
            )}
          </form>
        </div>
        
        {/* Results Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              {searchQuery || locationQuery 
                ? `Search Results${searchQuery ? ` for "${searchQuery}"` : ''}${locationQuery ? ` in "${locationQuery}"` : ''}` 
                : 'All Jobs'}
            </h2>
            <span className="text-sm text-gray-500">{filteredJobs.length} jobs found</span>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF8C00]"></div>
            </div>
          ) : filteredJobs.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredJobs.map(job => (
                  <JobCard 
                    key={`job-${job.id}`} // Ensure truly unique keys by adding a prefix
                    id={job.id}
                    title={job.title}
                    businessName={job.businessName}
                    businessLogo={job.businessLogo}
                    businessImagePath={job.businessImagePath}
                    location={job.location}
                    salary={job.salary}
                    postedDays={job.postedDays}
                    description={job.description}
                    address={job.address}
                    pay={job.pay}
                    businessId={job.businessId}
                    onApply={handleApplyJob}
                    immediateStart={job.immediateStart}
                    applicationUrl={job.applicationUrl}
                  />
                ))}
              </div>
              
              {hasMore && (
                <div className="mt-8 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-6 py-3 bg-white border border-[#FF8C00] text-[#FF8C00] rounded-lg hover:bg-[#FF8C00]/10 transition-colors font-medium disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#FF8C00]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading more...
                      </span>
                    ) : 'Load More Jobs'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <div className="bg-gray-100 p-6 inline-block rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">No jobs found</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6">
                We couldn't find any jobs matching your criteria. Try adjusting your search terms or filters.
              </p>
              <button
                onClick={resetFilters}
                className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobListPage; 